// Session adapter. Historically a SINGLE `maa-session` record owned by everyone; being
// split (Seq B.3) into domain-owned records so each domain reads/writes only its own:
//   - maa-session      → identity      { sessionId, timestamp }        (owns expiry)
//   - maa-live-agent   → LiveAgent     { isEscalated, conversationId, caseNumber, lastActivityAt }
//   - maa-drawer-prefs → Shell/drawer  { shouldAutoOpen, hasSessionInteractedWithFab, pagesExpandedFABShownOn }
//   - maa-conversation → Conversation  { lastSessionStart }            (message history's watermark)
// Rule: single WRITER per field; cross-READS are allowed.
//
// B.3a (this commit) adds the new keys/defaults/hooks but leaves getDefaultSession() flat and
// useMaaSession unchanged, so nothing breaks. Consumers migrate to the new hooks in field-sharing
// groups (B.3b live-agent, B.3c drawer-prefs); getDefaultSession shrinks to identity-only last.
import { createRecord, readRecord, useRecord } from './record';

export const MAA_SESSION_KEY = 'maa-session';
export const MAA_LIVE_AGENT_KEY = 'maa-live-agent';
export const MAA_DRAWER_PREFS_KEY = 'maa-drawer-prefs';
export const MAA_CONVERSATION_KEY = 'maa-conversation';

const FALLBACK_SESSION_PREFIX = 'fallback-sessionId';

/** Session id from the `thda.s` cookie, else a unique fallback. */
export const getSessionId = () => {
  if (typeof window !== 'undefined') {
    const cookieSessionId = window?.cookieUtils?.readBrowserCookie('thda.s');
    if (cookieSessionId) {
      return cookieSessionId;
    }
    const uuid = window?.crypto?.randomUUID?.() || Date.now();
    return `${FALLBACK_SESSION_PREFIX}-${uuid}`;
  }
  return `${FALLBACK_SESSION_PREFIX}-${Date.now()}`;
};

// Read-only access to the legacy single `maa-session` envelope so the split records can seed
// from it on the first load after the split ships — a returning user's escalation / drawer
// prefs survive without an imperative migration write. Once a domain's own record exists,
// that record's own value wins and this seed is ignored.
export const readLegacySessionRecord = () => readRecord(MAA_SESSION_KEY);

/**
 * The identity record: session id + creation timestamp (which owns the 24h expiry). The
 * live-agent connection fields and the drawer/FAB prefs that used to ride on this record now
 * live in their own domain records (getDefaultLiveAgentSession / getDefaultDrawerPrefs) — the
 * B.3 split is complete, so this is identity-only.
 */
export const getDefaultSession = () => ({
  sessionId: getSessionId(),
  timestamp: Date.now(),
});

/** LiveAgent connection default, seeded from any legacy flat record (else fresh). */
export const getDefaultLiveAgentSession = () => {
  const legacy = readLegacySessionRecord();
  return {
    isEscalated: legacy?.isEscalated ?? false,
    conversationId: legacy?.conversationId ?? null,
    caseNumber: legacy?.caseNumber ?? null,
    lastActivityAt: legacy?.lastActivityAt ?? null,
  };
};

/** Shell/drawer prefs default, seeded from any legacy flat record (else fresh). */
export const getDefaultDrawerPrefs = () => {
  const legacy = readLegacySessionRecord();
  return {
    shouldAutoOpen: legacy?.shouldAutoOpen ?? false,
    hasSessionInteractedWithFab: legacy?.hasSessionInteractedWithFab ?? false,
    pagesExpandedFABShownOn: legacy?.pagesExpandedFABShownOn ?? [],
  };
};

/**
 * Conversation-domain default. `lastSessionStart` is the identity `timestamp` message history
 * last initialized against — its watermark for "which session generation is this history from".
 * Null means it has never initialized, which must NOT read as a generation change.
 */
export const getDefaultConversation = () => ({ lastSessionStart: null });

// The records. Each is created once per document, so `get()` is a single synchronous
// authority every holder shares — there is no per-holder mirror to go stale, and no default to
// memoize at each call site.
//
// Only drawer prefs is `crossTab`. Identity must not follow a peer mid-page (see
// reconcileSession), and the live-agent record has its own cross-tab protocol on the
// BroadcastChannel plus an engine that reads it synchronously mid-conversation.
export const sessionRecord = createRecord({
  key: MAA_SESSION_KEY,
  buildDefault: getDefaultSession,
});

export const liveAgentRecord = createRecord({
  key: MAA_LIVE_AGENT_KEY,
  buildDefault: getDefaultLiveAgentSession,
});

export const drawerPrefsRecord = createRecord({
  key: MAA_DRAWER_PREFS_KEY,
  buildDefault: getDefaultDrawerPrefs,
  crossTab: true,
});

// Written ONLY by message history (hooks/useMessageHistory#init). Not `crossTab`: every
// document compares the watermark against identity for itself at init, so there is nothing a
// peer's write needs to tell a mounted tab — and a mid-page notification would invite exactly
// the re-entrant clear that `isNewSessionGeneration` exists to keep to mount time.
export const conversationRecord = createRecord({
  key: MAA_CONVERSATION_KEY,
  buildDefault: getDefaultConversation,
});

/** A session older than 24h (or with no timestamp) is expired. */
export const isSessionExpired = (sessionTimestamp) => {
  if (!sessionTimestamp) {
    return true;
  }
  const sessionAge = Date.now() - sessionTimestamp;
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
  return sessionAge > TWENTY_FOUR_HOURS;
};

/** Was this id minted by getSessionId's fallback, rather than read from `thda.s`? */
export const isFallbackSessionId = (sessionId) => (
  typeof sessionId === 'string' && sessionId.startsWith(`${FALLBACK_SESSION_PREFIX}-`)
);

// Ceiling that applies whatever the id kind. thda.s rotates rarely, so without this a
// cookie-derived session never resets and never clears what it accumulates: the drawer
// prefs (FAB expansion state) and the IndexedDB history pruned by sessionId.
export const isSessionStale = (sessionTimestamp) => {
  if (!sessionTimestamp) {
    return true;
  }
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  return (Date.now() - sessionTimestamp) > SEVEN_DAYS;
};

/**
 * Whether the identity record should be replaced. Evaluated on MOUNT only, so a user
 * mid-conversation is never reset out from under them.
 *
 * Which signal applies depends on how the id was minted: a fallback id has no external
 * rotation signal, so it ages out on the 24h timer; a thda.s id is owned by analytics, so
 * it is replaced when THAT value rotates rather than on the timer. The 7 day backstop
 * applies to either kind.
 */
export const shouldResetSession = (session) => {
  if (!session?.sessionId) {
    return true;
  }

  if (isFallbackSessionId(session.sessionId)) {
    return isSessionExpired(session.timestamp);
  }

  // A fallback here means thda.s was unreadable; never downgrade a real id to one.
  const currentSessionId = getSessionId();
  if (!isFallbackSessionId(currentSessionId) && currentSessionId !== session.sessionId) {
    return true;
  }

  return isSessionStale(session.timestamp);
};

/**
 * Has the identity been renewed since message history last initialized against it?
 *
 * `timestamp` IS the generation counter: the only writes to the identity record are `seed()`
 * (first ever), the `reconcileSession` adoption — which changes the timestamp only when it
 * changes the sessionId too, since preferSession short-circuits on equal ids — and the reset in
 * `shell/useDrawerController`. So a moved timestamp means a reset fired.
 *
 * This exists because the sessionId is NOT a reliable reset signal on its own. Two of the three
 * reset reasons change it (a fallback id ages out to a fresh uuid, a rotated thda.s brings a new
 * value), and the sessionId-keyed prune in `cleanOrphans` clears the conversation as a side
 * effect. The 7 day backstop does not: `getDefaultSession()` re-reads thda.s, so a shopper whose
 * cookie has not rotated is reset to the SAME id with a fresh timestamp, every row still matches,
 * and a conversation from the previous week survives a reset meant to end it. Comparing
 * timestamps catches that case and the other two alike.
 *
 * A null watermark is a history that has never initialized — a first visit, not a new
 * generation — and must not clear.
 *
 * @param {number|null} lastSessionStart - `maa-conversation.lastSessionStart`.
 * @param {number|null|undefined} sessionStart - the current `maa-session.timestamp`.
 */
export const isNewSessionGeneration = (lastSessionStart, sessionStart) => (
  lastSessionStart !== null
  && lastSessionStart !== undefined
  && lastSessionStart !== sessionStart
);

/**
 * Order-INDEPENDENT choice between two identity records. Tabs that observe each other's write
 * do so at unpredictable moments and in either order, so the merge rule has to be one they
 * both compute the same answer from — a last-write-wins rule lets two tabs ping-pong instead
 * of converging. A real thda.s id beats a minted fallback (the same rule shouldResetSession
 * applies when it refuses to downgrade one), and two ids of the same kind order
 * lexicographically, which is arbitrary but stable.
 */
export const preferSession = (mine, theirs) => {
  if (!mine?.sessionId) return theirs?.sessionId ? theirs : null;
  if (!theirs?.sessionId) return mine;
  if (mine.sessionId === theirs.sessionId) return mine;

  const mineIsFallback = isFallbackSessionId(mine.sessionId);
  const theirsIsFallback = isFallbackSessionId(theirs.sessionId);
  if (mineIsFallback !== theirsIsFallback) {
    return mineIsFallback ? theirs : mine;
  }
  return mine.sessionId < theirs.sessionId ? mine : theirs;
};

/**
 * Reconcile this tab's identity against what is actually in localStorage, returning the record
 * this tab should hold.
 *
 * Two tabs opening together can BOTH find the record absent on their first render and each
 * mint and persist a different fallback id. Only one write survives, and nothing tells the
 * loser, so both ids stay live and the backend sees two session ids for one visitor.
 * Re-reading resolves that, and because preferSession is order-independent every tab that
 * looks reaches the same answer whenever it happens to look.
 *
 * Call this at MOUNT only. Nothing in this tab has emitted with the id at that point, so
 * replacing it costs nothing; adopting one later would split an analytics trail mid-page and
 * unpin the conversation context the backend keys on. That is also why `sessionRecord` is not
 * a crossTab record: identity must not follow a peer's write mid-page.
 */
export const reconcileSession = (current) => {
  const persisted = readRecord(MAA_SESSION_KEY);
  if (!persisted?.sessionId) {
    return current;
  }
  return preferSession(current, persisted);
};

/** A per-action id derived from the session id (random when there is none). */
export const createActionId = (sessionId) => {
  if (!sessionId) {
    return `${crypto.randomUUID()}_${Date.now().toString()}`;
  }
  return `${sessionId}_${Date.now().toString()}`;
};

/** React binding for the `maa-session` (identity) record. Returns `[session, update]`. */
export const useMaaSession = () => useRecord(sessionRecord);

/** React binding for the `maa-live-agent` record. Returns `[liveAgent, update]`. */
export const useLiveAgentSession = () => useRecord(liveAgentRecord);

/** React binding for the `maa-drawer-prefs` record. Returns `[drawerPrefs, update]`. */
export const useDrawerPrefs = () => useRecord(drawerPrefsRecord);
